import { useId, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

/*
 * Logos are inline SVG rather than <img>, so they inherit the current text
 * colour, stay crisp at any size, and cost no extra request on a screen the
 * worker is already waiting on.
 */

/**
 * The KaProLean mark on its own — the same geometry as the app icon.
 *
 * Three round-capped strokes, not a filled letterform: at the sizes this
 * appears (24–40px in the shell) thin joins disappear.
 */
export function KaproMark({
  className,
  style,
}: {
  className?: string
  /** Used by the splash screen for its 3D transforms. */
  style?: CSSProperties
}) {
  /*
   * A unique gradient id per instance.
   *
   * This was a fixed string, and more than one mark renders at a time — the
   * collapsed sidebar rail shows a bare mark while `KaproLogo` (which contains
   * another) is still in the DOM, just hidden. Duplicate ids are invalid, the
   * browser resolves `url(#…)` to whichever came first in the document, and a
   * fill referencing a gradient it cannot resolve paints nothing at all. The
   * result was a mark with no blue tile behind it: white strokes on a white
   * sidebar, i.e. an apparently missing logo.
   */
  const gradientId = useId()

  return (
    <svg
      viewBox="0 0 512 512"
      className={cn('size-8', className)}
      style={style}
      role="img"
      aria-label="KaProLean"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
      </defs>

      <rect width="512" height="512" rx="114" fill={`url(#${gradientId})`} />

      <g fill="none" stroke="#fff" strokeWidth="56" strokeLinecap="round">
        <path d="M176 152 V360" />
        <path d="M206 256 L330 152" />
        <path d="M206 256 L336 360" strokeOpacity=".78" />
      </g>
    </svg>
  )
}

/**
 * Mark plus wordmark, for the shell header and the login screen.
 *
 * The wordmark is live text in the SVG's sibling, not paths — it stays
 * selectable, scales with the user's font settings, and does not need
 * regenerating if the product name ever changes.
 */
export function KaproLogo({
  className,
  showTagline = false,
  size = 'md',
}: {
  className?: string
  showTagline?: boolean
  size?: 'sm' | 'md' | 'lg'
}) {
  const t = useT()

  const marks = { sm: 'size-7', md: 'size-9', lg: 'size-12' }
  const words = { sm: 'text-base', md: 'text-lg', lg: 'text-2xl' }

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <KaproMark className={marks[size]} />

      <span className="leading-tight">
        {/*
          Split as Ka + Pro + Lean, following the name rather than the letters.
          "Lean" carries the accent because it is the part that is new and the
          part that says what the software is for.

          Written as three nodes on purpose — which also means a search for the
          literal string "KaProLean" will never find this line. The rename from
          KaPro missed exactly that: every other occurrence in both repositories
          was a plain string and this one, the most visible of the lot, was not.
        */}
        <span className={cn('font-heading block font-semibold tracking-tight', words[size])}>
          Ka<span className="text-brand-600">Pro</span>
          <span className="text-brand-400">Lean</span>
        </span>
        {showTagline && (
          <span className="muted block text-xs">{t('brand.tagline')}</span>
        )}
      </span>
    </span>
  )
}
